<template>
	<div class="cog-payment__field">
		<c-input v-if="!overrideInputStyles" ref="paymentStyle" style="display: none;" inputmode="numeric" placeholder="placeholder" />
		<slot :input-styles="inputStyles" :save-card="saveCard"></slot>
		<transition	name="cog-error-message">
			<div v-if="errorMessage" aria-live="assertive" role="alert" class="cog-form-payment-error cog-error-message">
				{{ errorMessage }}
				<a v-if="supportPageLinkUrl" :href="supportPageLinkUrl" target="_blank" class="matins">
					{{ supportPageLinkText || 'Learn how to fix this.' }}
				</a>
			</div>
		</transition>
		<c-field v-if="saveCard" v-slot="{ field }" source="Save_Card_Agreement" hide-label class="cog-payment__save-card">
			<c-checkbox
				v-model="field.value"
				:label="$resource('card-on-file-agreement', { orgname: session.organizationName })"
				:described-by="cardOwnerNotice ? cardOwnerNoticeId : null"
			/>
		</c-field>
		<div v-if="cardOwnerNotice" :id="cardOwnerNoticeId" class="cog-payment__card-owner-notice cog-warning-message">
			{{ cardOwnerNotice }}
		</div>
	</div>
</template>

<script>
	import { getFormStyles } from './payment-form-style';
	import { readEntryText, describeSubmitter, isSubmitterMatch, looksLikeEmail } from './submitter-match';
	import CInput from '../Input.vue';
	import CCheckbox from '../Checkbox.vue';
	import { FormEvents } from 'src/framework/public/events';
	import 'src/style/_warning-message.scss';

	let paymentInstance = 0;

	export default {
		name: 'CPayment',
		components: {
			CInput,
			CCheckbox
		},
		inject: {
			$resource: {
				from: '$resource',
				required: true
			},
			formEvents: {
				from: 'formEvents',
				default: null
			},
			session: {
				from: 'session',
				required: true
			}
		},
		props: {
			entry: { type: Object, required: true },
			saveCard: { type: Boolean, default: false },
			errorMessage: { type: String, default: '' },
			supportPageLinkUrl: { type: String, default: '' },
			supportPageLinkText: { type: String, default: '' },
			useThemeSettings: { type: Boolean, default: true },
			overrideInputStyles: { type: Object, default: null },
			submitterNamePath: { type: String, default: '' },
			submitterEmailPath: { type: String, default: '' },
			linkedByEmail: { type: Boolean, default: false }
		},
		data() {
			return {
				styleReference: null,
				themeSettings: { ...this.entry.Form_Theme },
				cardOwnerNoticeId: `cog-payment-card-owner-notice-${++paymentInstance}`
			};
		},
		computed: {
			inputStyles() {
				return this.overrideInputStyles ? this.overrideInputStyles : this.getFormStyles();
			},
			submitterEmail() {
				return readEntryText(this.entry, this.submitterEmailPath);
			},
			submitterProfile() {
				// Who the card is being saved to, as "Name (email)". Empty until a whole email is on a form linked by email
				if (this.linkedByEmail && !looksLikeEmail(this.submitterEmail))
					return '';

				return describeSubmitter(readEntryText(this.entry, this.submitterNamePath), this.submitterEmail);
			},
			isSubmitter() {
				// Whether the respondent is the person the card is being saved to
				return isSubmitterMatch(this.session.userInfo && this.session.userInfo.Email, this.submitterEmail);
			},
			cardOwnerNotice() {
				// Shown when the card is being saved to someone other than the respondent
				if (!this.saveCard || !this.submitterEmailPath || !this.submitterProfile || this.isSubmitter)
					return '';

				return this.$resource('card-on-file-other-person', { name: this.submitterProfile });
			}
		},
		mounted() {
			this.styleReference = this.getPaymentStyleElement();

			if (this.formEvents) {
				this.formEvents.on(FormEvents.ThemeUpdated, (evt) => {
					Object.assign(this.themeSettings, evt.data.themeSettings);
					const addedPaymentInputStyles = this.$el.querySelector('.payment-input-styles');
					if (addedPaymentInputStyles)
						addedPaymentInputStyles.remove();
					this.addPaymentInputStyles();
				});
			}

			if (this.styleReference)
				this.addPaymentInputStyles();
		},
		methods: {
			/**
			 * Gets a map of element styles for payment fields
			 */
			getFormStyles() {
				if (this.styleReference)
					return getFormStyles(this.styleReference, this.useThemeSettings ? this.themeSettings : {});
				return {};
			},

			/**
			 * Gets the element from which to derive styles for payment fields
			 * @returns Element
			 */
			getPaymentStyleElement() {
				if (this.$refs.paymentStyle) {
					const paymentStyleComponent = this.$refs.paymentStyle;
					if (paymentStyleComponent) {
						const paymentStyleInput = paymentStyleComponent.$el.querySelector('input');
						if (paymentStyleInput) {
							return paymentStyleInput;
						}
						else {
							console.log('DEBUG: No payment style input found');
						}
					}
				}
			},

			/**
			 * Add text input styles to Stripe/Square divs
			 */
			addPaymentInputStyles() {
				const inputStyles = this.getFormStyles();
				const nativeBorderStyles = inputStyles.computedStyles.border.includes('2px inset');

				const includedStyles = [
					'background-color',
					'background-image',
					'background-position',
					'background-repeat',
					'background-size',

					'border-top-color',
					'border-top-left-radius',
					'border-top-right-radius',
					'border-top-style',
					'border-top-width',

					'border-right-color',
					'border-right-style',
					'border-right-width',

					'border-bottom-color',
					'border-bottom-left-radius',
					'border-bottom-right-radius',
					'border-bottom-style',
					'border-bottom-width',

					'border-left-color',
					'border-left-style',
					'border-left-width',

					'box-shadow',

					'padding-top',
					'padding-right',
					'padding-bottom',
					'padding-left'
				].reduce(function(includedStyles, styleProperty) {
					let styleValue = inputStyles.computedStyles[styleProperty];

					// transform reported native border style (2px inset) to what it actually looks like (1px solid).
					if (nativeBorderStyles && styleProperty.includes('border')) {
						if (styleProperty.includes('-style'))
							styleValue = 'solid';
						else if (styleProperty.includes('-width'))
							styleValue = '1px';
					}
					includedStyles += styleProperty + ':' + styleValue + ';';
					return includedStyles;
				}, '');

				const inputStyleBlock = document.createElement('style');
				inputStyleBlock.className = 'payment-input-styles';
				inputStyleBlock.textContent = `:root:root:root:root:root .cog-payment__field .cog-payment__frame .cog-input > div{${includedStyles}}`;
				this.$el.appendChild(inputStyleBlock);
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {
	@include at-root(2) {

		.cog-cognito--chameleon .StripeElement--focus {
			border-color: var(--highlight);
			box-shadow: var(--input-focus__box-shadow);
		}
	}

	.cog-payment__frame .cog-input iframe {
		// Prevent spacing below iframe due to default inline display
		display: block;
	}

	.cog-input__placeholder {
		@include input-essential;
		color: var(--placeholder__color);
	}

	// Override spacing around normal fields
	.cog-field.cog-payment__save-card.cog-payment__save-card {
		width: 100%;
		margin: 0;
		transition: padding $speed-half, margin $speed-half;

		&.is-error {
			margin-top: var(--gutter);
		}

		.el-checkbox__input {
			align-self: flex-start;
			height: calc(var(--line-height) * 1em);
		}
	}

	// When the checkbox is not in error it should line up with CC inputs
	.cog-field:not(.is-error).cog-payment__save-card {
		padding: 0;
	}

	.cog-payment__card-owner-notice {
		margin-top: $gutter-quarter;
		transition: margin $speed-half;
	}

	.cog-payment__save-card.is-error.is-required + .cog-payment__card-owner-notice {
		margin-top: var(--gutter);
	}
}
</style>
